import clsx from 'clsx';
import { ToastManagerContext } from 'components/ToastManager';
import { OnboardBnkDevEnv } from 'hooks/internalApi';
import { useContextSafely } from 'hooks/useContextSafely';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FragmentEnv } from 'utils/env';
import { putLocalStorage } from 'utils/localStorage';
import { getSessionStorage } from 'utils/sessionStorage';
import { v4 } from 'uuid';
import { useLocation } from 'wouter';
import { useWorkspaceId } from 'wrappers/WorkspaceProvider';

import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';
import { LinkIcon } from '@fragment/ui/src/components/Icon/LinkIcon/LinkIcon';
import { InlineError } from '@fragment/ui/src/components/InlineError/InlineError';
import { ModalField } from '@fragment/ui/src/components/Modal/ModalField';
import { ModalTextInput } from '@fragment/ui/src/components/Modal/ModalTextInput';
import { Toast } from '@fragment/ui/src/components/Toast';
import { Toggle } from '@fragment/ui/src/components/Toggle/Toggle';

import { DocsLink } from './DocsLink';
import { ToggleField } from './ToggleField';

type FormData = {
  name: string;
  env: OnboardBnkDevEnv;
};

export const OnboardIncreaseLinkForm = () => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { isValid, errors },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      env: OnboardBnkDevEnv.Sandbox,
    },
  });
  const workspaceId = useWorkspaceId();
  const watchEnv = watch('env', OnboardBnkDevEnv.Sandbox);

  const authorizeWithIncrease = (data: FormData) => {
    const { name, env } = data;
    const state = v4();
    const increaseRedirect = `https://increase.com/oauth/authorization?client_id=${FragmentEnv.increase.clientId}&state=${state}&scope=read_write`;
    putLocalStorage('increase_onboard_payload', {
      name,
      env,
      state,
      workspaceId,
    });
    window.location.href = increaseRedirect;
  };

  const { showToast } = useContextSafely(ToastManagerContext);
  const [location, unusedSetLocation] = useLocation();
  useEffect(() => {
    const onboardError = getSessionStorage('onboard_increase_error_message');
    if (onboardError) {
      showToast(<Toast message={onboardError} type="error" />);
    }
  }, [showToast]);

  return (
    <form
      // eslint-disable-next-line @typescript-eslint/no-misused-promises -- grandfathered
      onSubmit={handleSubmit((data) => {
        authorizeWithIncrease(data);
      })}
    >
      <div className="space-y-f2">
        <div className={clsx('flex flex-row justify-between')}>
          <div className="flex items-center space-x-[2px]">
            <LinkIcon type="IncreaseLink" />
            <span>Increase</span>
          </div>
          <DocsLink linkType="IncreaseLink" />
        </div>
        <div className="space-y-f2">
          <div className="flex flex-col gap-y-[1px]">
            <ModalField label="Name" hasError={!!errors.name}>
              <ModalTextInput
                // To keep backwards compatibility with the existing behavior
                placeholder={
                  watchEnv === OnboardBnkDevEnv.Production
                    ? 'Increase'
                    : 'Increase sandbox'
                }
                hasError={!!errors.name}
                {...register('name', {
                  required: {
                    value: true,
                    message: 'Enter a name for your Increase integration',
                  },
                  maxLength: {
                    value: 128,
                    message:
                      'Integration names should be shorter than 128 characters',
                  },
                })}
              />
            </ModalField>
            <ToggleField label="Environment">
              <Toggle
                inline
                onLeftClick={() => setValue('env', OnboardBnkDevEnv.Sandbox)}
                onRightClick={() =>
                  setValue('env', OnboardBnkDevEnv.Production)
                }
                leftLabel="Sandbox"
                buttonHeight="h-f2"
                buttonPadding="p-f0"
                rightLabel="Production"
                initialActiveButton="left"
              />
            </ToggleField>
          </div>
          {errors.name?.message && (
            <InlineError singleLine>{errors.name.message}</InlineError>
          )}
          <PrimaryButtonV2 disabled={!isValid} type="submit">
            Add Link
          </PrimaryButtonV2>
        </div>
      </div>
    </form>
  );
};
